/**
 * Activity Tracker Composable
 *
 * Tracks user activity to enable inactivity-based session timeout.
 * Used by the auth store to determine if token refresh should proceed.
 *
 * Security: If user is inactive for the configured timeout period,
 * they will be logged out when their access token expires.
 */

const STORAGE_KEY = 'zaveato_last_activity'
const DEBOUNCE_MS = 30000 // Update at most every 30 seconds
const DEFAULT_TIMEOUT_MS = 4 * 60 * 60 * 1000 // 4 hours - generous for admin users reading reports

// Track if listeners are already set up (singleton pattern)
let listenersInitialized = false
let debounceTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Update the last activity timestamp (debounced)
 */
function updateActivity(): void {
  // Debounce: only update if enough time has passed
  if (debounceTimer) return

  const now = Date.now()
  localStorage.setItem(STORAGE_KEY, now.toString())

  // Set debounce timer
  debounceTimer = setTimeout(() => {
    debounceTimer = null
  }, DEBOUNCE_MS)
}

/**
 * Get the last activity timestamp
 */
function getLastActivity(): number {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored) {
    const timestamp = parseInt(stored, 10)
    if (!isNaN(timestamp)) {
      return timestamp
    }
  }
  // If no activity recorded, return current time (user just started)
  const now = Date.now()
  localStorage.setItem(STORAGE_KEY, now.toString())
  return now
}

/**
 * Check if user has been inactive for longer than the specified timeout
 */
function isInactive(timeoutMs: number = DEFAULT_TIMEOUT_MS): boolean {
  const lastActivity = getLastActivity()
  const now = Date.now()
  const inactiveTime = now - lastActivity
  return inactiveTime > timeoutMs
}

/**
 * Get the time in milliseconds since last activity
 */
function getInactiveTime(): number {
  const lastActivity = getLastActivity()
  return Date.now() - lastActivity
}

/**
 * Initialize activity tracking event listeners
 * Should be called once when the app starts
 */
function initializeTracking(): void {
  if (listenersInitialized) return
  if (typeof window === 'undefined') return

  // Events that indicate user activity
  const activityEvents = [
    'mousedown',
    'keydown',
    'scroll',
    'touchstart',
    'click'
  ]

  // Add passive listeners for better performance
  activityEvents.forEach(event => {
    window.addEventListener(event, updateActivity, { passive: true })
  })

  // Also track visibility changes - user coming back to tab
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // User returned to tab - but don't update activity automatically
      // They need to actually interact to reset the timer
    }
  })

  // Set initial activity timestamp
  updateActivity()

  listenersInitialized = true
  console.log('✅ Activity tracking initialized')
}

/**
 * Reset the activity timestamp (useful after successful login)
 */
function resetActivity(): void {
  const now = Date.now()
  localStorage.setItem(STORAGE_KEY, now.toString())
  // Clear debounce timer to allow immediate update
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
}

/**
 * Clear activity tracking data (useful on logout)
 */
function clearActivity(): void {
  localStorage.removeItem(STORAGE_KEY)
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
}

/**
 * Composable for activity tracking
 */
export function useActivityTracker() {
  return {
    initializeTracking,
    isInactive,
    getInactiveTime,
    getLastActivity,
    resetActivity,
    clearActivity,
    INACTIVITY_TIMEOUT: DEFAULT_TIMEOUT_MS
  }
}

export default useActivityTracker
